const RED = 0.2126;
const GREEN = 0.7152;
const BLUE = 0.0722;

const GAMMA = 2.4;

export const luminance = (r: number, g: number, b: number) => {
  const a = [r, g, b].map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, GAMMA);
  });
  return a[0] * RED + a[1] * GREEN + a[2] * BLUE;
};

export const contrast = (lum1: number, lum2: number) => {
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  return (brightest + 0.05) / (darkest + 0.05);
};

// --- RGB <-> HSL + theme-derived colors -----------------------------------
//
// App theme colors are stored as `"r, g, b"` triplet strings (e.g.
// "102, 229, 217") and consumed in CSS as `rgb(var(--x))` / `rgba(var(--x), a)`.
// The helpers below parse that format and convert to/from HSL so a derived
// color (currently only `--private-color`) can be computed from the user's
// configurable accent/primary at theme-apply time. Hue is in degrees [0,360),
// saturation/lightness in [0,1].

export type Hsl = { h: number; s: number; l: number };

/** Parses a `"r, g, b"` triplet (tolerant of surrounding whitespace / newlines). */
export const parseRgbTriplet = (
  value: string | undefined | null
): [number, number, number] | null => {
  if (!value) return null;
  const parts = value.split(',').map((p) => Number.parseInt(p.trim(), 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  return [parts[0], parts[1], parts[2]];
};

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

export const rgbToHsl = (r: number, g: number, b: number): Hsl => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const delta = max - min;
  if (delta === 0) return { h: 0, s: 0, l };
  const s = delta / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === r) h = ((g - b) / delta) % 6;
  else if (max === g) h = (b - r) / delta + 2;
  else h = (r - g) / delta + 4;
  h *= 60;
  if (h < 0) h += 360;
  return { h, s, l };
};

export const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
  h = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let rp = 0;
  let gp = 0;
  let bp = 0;
  if (h < 60) [rp, gp, bp] = [c, x, 0];
  else if (h < 120) [rp, gp, bp] = [x, c, 0];
  else if (h < 180) [rp, gp, bp] = [0, c, x];
  else if (h < 240) [rp, gp, bp] = [0, x, c];
  else if (h < 300) [rp, gp, bp] = [x, 0, c];
  else [rp, gp, bp] = [c, 0, x];
  return [Math.round((rp + m) * 255), Math.round((gp + m) * 255), Math.round((bp + m) * 255)];
};

// Weighted circular mean of two hues. `weight2` (0..1) scales how strongly
// `h2` pulls the result toward itself — used to fade out a near-grey color's
// (meaningless) hue.
const blendHue = (h1: number, h2: number, weight2: number): number => {
  const rad = (d: number) => (d * Math.PI) / 180;
  const x = Math.cos(rad(h1)) + weight2 * Math.cos(rad(h2));
  const y = Math.sin(rad(h1)) + weight2 * Math.sin(rad(h2));
  let h = (Math.atan2(y, x) * 180) / Math.PI;
  if (h < 0) h += 360;
  return h;
};

// Pushes `h` away from `ref` until they are at least `gap` degrees apart
// (shortest signed direction), keeping the side `h` is already on.
const enforceMinHueGap = (h: number, ref: number, gap: number): number => {
  const diff = ((h - ref + 540) % 360) - 180; // signed shortest diff in (-180, 180]
  if (Math.abs(diff) >= gap) return h;
  const sign = diff === 0 ? 1 : Math.sign(diff);
  return (((ref + sign * gap) % 360) + 360) % 360;
};

// Tunables. Defaults chosen so the stock themes resolve to a pleasant violet
// (close to the previous hard-coded private color), while staying legible and
// distinct from the accent for arbitrary user palettes.
const PRIVATE_MIN_ACCENT_HUE_GAP = 45; // min hue distance kept from the accent
const PRIVATE_SATURATION_RANGE: [number, number] = [0.55, 0.95];
const PRIVATE_LIGHTNESS_RANGE: [number, number] = [0.52, 0.68];
// The result is confined to a violet->magenta band — the conventional
// "private / whisper" family. This is what keeps the color always
// DM-appropriate and, critically, off the semantic status hues (a naive
// accent+primary average can land on green/amber/red — e.g. cyan + orange
// averages to green, which would read as "online/success"). Both brand hues
// still shift the result *within* this band, and the theme drives the
// saturation/lightness below, so it harmonizes without ever going odd.
const PRIVATE_DM_HUE_BASE = 270; // violet
const PRIVATE_DM_HUE_SPREAD = 50; // theme hue maps across base..base+spread

/**
 * Derives the private-message identity color (`--private-color`) from the
 * theme's accent and primary colors. Returns a `"r, g, b"` triplet, or `null`
 * if the accent can't be parsed (caller should leave the static fallback).
 *
 * Strategy (see plan / AskUserQuestion "Smart blend of both"):
 * - Combine the accent + primary hues into one "theme hue" (the primary is
 *   weighted by its own saturation, so a near-grey primary contributes
 *   nothing and the accent dominates).
 * - Map that theme hue into a fixed violet->magenta band so the result is
 *   always DM-appropriate and never collides with the green/amber/red status
 *   colors, while still shifting with the user's palette.
 * - Nudge it to keep a minimum gap from the accent (covers a violet/magenta
 *   accent, which remote bubbles / poke-mute stripes already use).
 * - Saturation is floored and lightness banded so the stripe/chip stay
 *   visible on both light and dark backgrounds.
 */
export const derivePrivateColor = (
  accent: string | undefined | null,
  primary: string | undefined | null
): string | null => {
  const accentRgb = parseRgbTriplet(accent);
  if (!accentRgb) return null;
  const a = rgbToHsl(accentRgb[0], accentRgb[1], accentRgb[2]);
  const primaryRgb = parseRgbTriplet(primary);
  const p = primaryRgb ? rgbToHsl(primaryRgb[0], primaryRgb[1], primaryRgb[2]) : null;

  // Both colors inform the hue; a grey primary (low saturation) fades out.
  const themeHue = p ? blendHue(a.h, p.h, p.s) : a.h;
  // Confine to the violet->magenta DM band, then keep clear of the accent.
  let h = PRIVATE_DM_HUE_BASE + (themeHue / 360) * PRIVATE_DM_HUE_SPREAD;
  h = enforceMinHueGap(h, a.h, PRIVATE_MIN_ACCENT_HUE_GAP);

  const s = clamp(
    Math.max(a.s, p?.s ?? 0),
    PRIVATE_SATURATION_RANGE[0],
    PRIVATE_SATURATION_RANGE[1]
  );
  const l = clamp(a.l, PRIVATE_LIGHTNESS_RANGE[0], PRIVATE_LIGHTNESS_RANGE[1]);

  const [r, g, b] = hslToRgb(h, s, l);
  return `${r}, ${g}, ${b}`;
};

// Chroma below this (0..1) counts as grey: its hue is noise. Chroma rather
// than HSL saturation, because near-black/near-white values report a high
// saturation (e.g. "10, 0, 0" has s = 1) although they read as neutral.
const VIVID_MIN_CHROMA = 0.08;
const VIVID_SATURATION_RANGE: [number, number] = [0.45, 0.85];
// Readable against the mode's `--background` (black / near-white).
const VIVID_LIGHTNESS = { dark: 0.62, light: 0.42 } as const;

const chroma = ([r, g, b]: [number, number, number]) =>
  (Math.max(r, g, b) - Math.min(r, g, b)) / 255;

/**
 * Derives `--primary-vivid` — the primary's hue at a lightness that stays
 * visible on the mode's background — for lines, glows and selected states.
 * Admins tend to pick a primary close to the background (near-black in dark
 * mode, near-white in light mode), which is unusable as a stroke color, so
 * only its hue survives. A grey primary has no hue to keep: the accent is used
 * instead. Returns a `"r, g, b"` triplet, or `null` if neither color parses.
 */
export const derivePrimaryVivid = (
  primary: string | undefined | null,
  accent: string | undefined | null,
  mode: 'dark' | 'light'
): string | null => {
  const primaryRgb = parseRgbTriplet(primary);
  const accentRgb = parseRgbTriplet(accent);
  const chromatic = [primaryRgb, accentRgb].find(
    (rgb): rgb is [number, number, number] => !!rgb && chroma(rgb) >= VIVID_MIN_CHROMA
  );
  const source = chromatic ?? primaryRgb ?? accentRgb;
  if (!source) return null;

  const { h, s } = rgbToHsl(source[0], source[1], source[2]);
  const saturation = chromatic ? clamp(s, VIVID_SATURATION_RANGE[0], VIVID_SATURATION_RANGE[1]) : 0;
  const [r, g, b] = hslToRgb(h, saturation, VIVID_LIGHTNESS[mode]);
  return `${r}, ${g}, ${b}`;
};
